import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, type PaidMediaPlanResponse, type CampanhaNaTela } from '../lib/api';
import { useResource } from '../lib/useResource';
import { formatNumber } from '../lib/dataState';
import { formatInstant } from '../lib/format';
import { Confirmacao } from '../components/Confirmacao';

/**
 * O detalhe do plano: o portão inteiro, a aprovação e a criação da campanha.
 *
 * Fonte: Paid Media Module SPEC v1.0 §6 (passos 6 a 10), §14 (as treze
 * checagens) e §16 (o envelope); Console SPEC v1.5 §247 (o checklist do portão)
 * e §248 (a tela de aprovação).
 *
 * Três propriedades que esta tela existe para manter:
 *
 * 1. **a recusa mostra a CADEIA inteira.** As catorze linhas aparecem todas, as
 *    que passaram e as que não passaram, cada uma com o motivo e o que fazer.
 *    Mostrar só o primeiro bloqueio devolveria quem opera ao regime de
 *    descobrir um impedimento por tentativa, que é o que faz a primeira
 *    campanha demorar uma semana;
 *
 * 2. **quem decide é o servidor.** O botão de criar campanha fica desligado
 *    quando o PORTÃO recusou, e não quando a tela achou que recusaria. A
 *    diferença aparece no dia em que a regra mudar: uma tela que deduz política
 *    passa a liberar o que o servidor recusa;
 *
 * 3. **toda ação que gasta pede confirmação com NÚMERO.** Quanto sai do
 *    envelope, quanto sobra depois, e o valor que a campanha vai gastar. O
 *    número confirmado volta ao servidor, que recusa se o valor mudou entre
 *    olhar e clicar.
 */

export function PaidMediaPlan() {
  const { id = '' } = useParams<{ id: string }>();
  const [versao, setVersao] = useState(0);
  const recarregar = useCallback(() => setVersao((v) => v + 1), []);
  const recurso = useResource<PaidMediaPlanResponse>(() => api.paidMediaPlan(id), [id, versao]);

  if (recurso.kind === 'loading') {
    return (
      <div className="text-sm" aria-busy="true">
        <h1 className="text-2xl font-bold">Plano de mídia paga</h1>
        <p className="mt-2 text-ink-muted">Carregando…</p>
      </div>
    );
  }

  if (recurso.kind === 'error') {
    return (
      <div className="max-w-3xl space-y-4 text-sm">
        <h1 className="text-2xl font-bold">Plano de mídia paga</h1>
        <div className="mt-4 rounded border border-danger-border bg-danger-surface p-4">
          <p className="font-medium">Não foi possível carregar este plano.</p>
          <p className="mt-1">{recurso.message}</p>
          <p className="mt-2 text-xs text-ink-faint">Código: {recurso.code}</p>
        </div>
        <p className="mt-3">
          <Link className="underline" to="/aquisicao">
            Voltar para mídia paga
          </Link>
        </p>
      </div>
    );
  }

  const { plan, gate, envelope, budget_impact: impacto, provider_configured } = recurso.data;
  const decidido = plan.status === 'APPROVED' || plan.status === 'REJECTED';

  return (
    <div className="space-y-4 text-sm">
      <header>
        <p className="text-xs">
          <Link className="underline" to="/aquisicao">
            Mídia paga
          </Link>
        </p>
        <h1 className="mt-1 text-2xl font-bold">{plan.content?.topic ?? 'Peça não encontrada'}</h1>
        <p className="mt-1 text-ink-muted">
          Plano {plan.status_label} · {plan.budget.formatado} {plan.budget_kind_label} · objetivo{' '}
          {plan.objective}
        </p>
      </header>

      <Proposta dados={recurso.data} />
      <Portao gate={gate} />

      <section className="mt-8" aria-labelledby="orcamento">
        <h2
          id="orcamento"
          className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
        >
          Orçamento
        </h2>
        {envelope.exists ? (
          <dl className="mt-3 grid gap-3 sm:grid-cols-3">
            <div className="rounded border border-line p-3">
              <dt className="text-xs text-ink-muted">Teto aprovado do mês</dt>
              <dd className="mt-1">
                <strong>{envelope.limit?.formatado}</strong>
              </dd>
            </div>
            <div className="rounded border border-line p-3">
              <dt className="text-xs text-ink-muted">Já reservado</dt>
              <dd className="mt-1">
                <strong>{envelope.used?.formatado}</strong>
              </dd>
            </div>
            <div className="rounded border border-line p-3">
              <dt className="text-xs text-ink-muted">Disponível agora</dt>
              <dd className="mt-1">
                <strong>{envelope.available?.formatado}</strong>
              </dd>
            </div>
          </dl>
        ) : (
          <p className="mt-3 rounded border border-warn-border bg-warn-surface p-3">
            {envelope.reason}
          </p>
        )}

        {impacto.reason === null ? (
          <p className="mt-2 text-xs text-ink-muted">
            Esta campanha reserva {impacto.reserve.formatado} e deixa{' '}
            {impacto.available_after?.formatado} no envelope.
          </p>
        ) : (
          <p className="mt-2 text-xs text-warn-ink">{impacto.reason}</p>
        )}
      </section>

      {decidido ? null : (
        <Decisao
          planId={plan.plan_id}
          valorMinor={plan.budget.minor}
          valorFormatado={plan.budget.formatado}
          disponivelAntes={impacto.available_before?.formatado ?? null}
          disponivelDepois={impacto.available_after?.formatado ?? null}
          aoDecidir={recarregar}
        />
      )}

      {plan.status === 'APPROVED' ? (
        <CriarCampanha
          planId={plan.plan_id}
          valorMinor={plan.budget.minor}
          valorFormatado={plan.budget.formatado}
          tipo={plan.budget_kind_label}
          disponivelAntes={impacto.available_before?.formatado ?? null}
          disponivelDepois={impacto.available_after?.formatado ?? null}
          contaSugerida={plan.meta_audience_binding?.meta_ad_account_id ?? ''}
          /*
            O que desliga o botão são FATOS do servidor: o portão recusou, ou não
            existe conexão com a Meta neste ambiente. Nenhuma política é deduzida
            aqui.
          */
          portaoPassou={gate.decision === 'pass'}
          provedorConfigurado={provider_configured}
          motivoDoPortao={gate.reason}
          aoCriar={recarregar}
        />
      ) : null}

      <Campanhas
        campanhas={plan.campaigns}
        provedorConfigurado={provider_configured}
        aoAtualizar={recarregar}
      />
    </div>
  );
}

// ===========================================================================
// A proposta (§248)
// ===========================================================================

function Proposta({ dados }: { dados: PaidMediaPlanResponse }) {
  const p = dados.plan;
  return (
    <section className="mt-6" aria-labelledby="proposta">
      <h2 id="proposta" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        O que vai acontecer
      </h2>
      <dl className="mt-3 space-y-2">
        <Linha rotulo="Peça">
          {p.content === null ? (
            <span className="text-ink-faint">não encontrada (o portão recusa por isso)</span>
          ) : (
            `${p.content.topic} · estado ${p.content.state}`
          )}
        </Linha>
        <Linha rotulo="Audiência">
          {p.audience === null ? (
            <span className="text-ink-faint">segmento não encontrado</span>
          ) : (
            <>
              {p.audience.name} ({p.audience.status}) ·{' '}
              {p.audience.eligible_record_count === null ? (
                <span className="text-ink-faint">nunca avaliado, e isso não é zero</span>
              ) : (
                `${formatNumber(p.audience.eligible_record_count)} elegíveis`
              )}
            </>
          )}
        </Linha>
        <Linha rotulo="Audiência na Meta">
          {p.meta_audience_binding === null ? (
            <span className="text-ink-muted">
              nenhuma selecionada, então a checagem de Custom Audience não se aplica
            </span>
          ) : (
            `${p.meta_audience_binding.audience_name} (${p.meta_audience_binding.status})` +
            (p.meta_audience_binding.last_sync_at === null
              ? ' · nunca sincronizada'
              : ` · sincronizada em ${formatInstant(p.meta_audience_binding.last_sync_at)}`)
          )}
        </Linha>
        <Linha rotulo="Objetivo">
          {p.objective}{' '}
          <span className="text-xs text-ink-faint">
            (escolhido sob {p.objective_capability_version || 'nenhuma versão de capacidade'})
          </span>
        </Linha>
        <Linha rotulo="Orçamento">
          {p.budget.formatado} {p.budget_kind_label}
          {p.start_at === null ? '' : ` · começa em ${formatInstant(p.start_at)}`}
          {p.end_at === null ? '' : ` · termina em ${formatInstant(p.end_at)}`}
        </Linha>
        <Linha rotulo="Direito de uso em mídia paga">{p.rights_paid_permission_label}</Linha>
        <Linha rotulo="Governança">
          {p.governance.triggers.length === 0
            ? 'nada mudou na peça: a governança orgânica continua valendo'
            : p.governance.pending
              ? `precisa de reavaliação: ${p.governance.triggers.join(', ')}`
              : `reavaliada em ${formatInstant(p.governance.cleared_at ?? '')}`}
        </Linha>
        <Linha rotulo="Proposto por">
          {p.created_by_agent} em {formatInstant(p.created_at)}
        </Linha>
        {p.decision_reason === null ? null : (
          <Linha rotulo="Decisão">
            {p.approved_by === null
              ? `recusado por ${p.rejected_by}`
              : `aprovado por ${p.approved_by}`}
            : {p.decision_reason}
          </Linha>
        )}
      </dl>
    </section>
  );
}

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-2 border-b border-line pb-1">
      <dt className="w-56 text-ink-muted">{rotulo}</dt>
      <dd className="grow">{children}</dd>
    </div>
  );
}

// ===========================================================================
// O portão (§247)
// ===========================================================================

function Portao({ gate }: { gate: PaidMediaPlanResponse['gate'] }) {
  return (
    <section className="mt-8" aria-labelledby="portao">
      <h2 id="portao" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Portão de distribuição paga
      </h2>
      <p className="mt-1 text-xs text-ink-muted">
        {/*
          Dizer que é prévia é parte do dado: quem lê precisa saber que o portão
          é reavaliado de verdade na hora de criar e na hora de ativar, e que um
          resultado verde aqui não dispensa a revalidação de lá.
        */}
        Conferido em {formatInstant(gate.evaluated_at)}. É uma prévia: o portão é avaliado de novo,
        inteiro, no momento de criar a campanha e no momento de ativar.
      </p>

      <p
        className={
          gate.decision === 'pass'
            ? 'mt-3 rounded-[11px] border border-line bg-surface-sunken p-3'
            : 'mt-3 rounded border border-warn-border bg-warn-surface p-3'
        }
      >
        <strong>
          {gate.decision === 'pass'
            ? `As ${formatNumber(gate.total_checks)} verificações passaram.`
            : `${formatNumber(gate.failed_checks)} de ${formatNumber(gate.total_checks)} verificações reprovaram.`}
        </strong>{' '}
        {gate.decision === 'pass'
          ? 'Criar a campanha vai reavaliar todas de novo.'
          : 'Todas aparecem abaixo, e não só a primeira: quem opera precisa saber tudo que falta de uma vez.'}
      </p>

      <ul className="mt-3 space-y-2" data-testid="checagens-do-portao">
        {gate.checks.map((c) => (
          <li
            key={c.codigo}
            className={
              c.passou ? 'rounded border border-line p-3' : 'rounded border border-warn-border p-3'
            }
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium">{c.linha}</span>
              {/*
                O sinal textual não depende de cor: "passou" e "reprovou" são
                lidos igual em monocromático e por leitor de tela.
              */}
              <span className="text-xs">{c.passou ? 'passou' : 'reprovou'}</span>
            </div>
            <p className="mt-1 text-xs text-ink-muted">{c.detalhe}</p>
            {c.o_que_fazer === null ? null : (
              <p className="mt-1 text-xs">
                <strong>O que fazer:</strong> {c.o_que_fazer}
              </p>
            )}
          </li>
        ))}
      </ul>

      {gate.release_source === 'nunca_avaliado' ? (
        <p className="mt-3 text-xs text-ink-muted">
          O Release Gate nunca foi avaliado para esta peça, e ausência de avaliação não é liberação.
          Ele é avaliado de verdade na criação da campanha.
        </p>
      ) : (
        <p className="mt-3 text-xs text-ink-muted">
          O resultado de release acima é o último registrado
          {gate.release_evaluated_at === null
            ? ''
            : ` (de ${formatInstant(gate.release_evaluated_at)})`}
          , e não uma avaliação feita agora.
        </p>
      )}
    </section>
  );
}

// ===========================================================================
// Aprovar ou recusar (§6 passo 8, §28)
// ===========================================================================

function Decisao({
  planId,
  valorMinor,
  valorFormatado,
  disponivelAntes,
  disponivelDepois,
  aoDecidir,
}: {
  planId: string;
  valorMinor: number;
  valorFormatado: string;
  disponivelAntes: string | null;
  disponivelDepois: string | null;
  aoDecidir: () => void;
}) {
  const [modo, setModo] = useState<'nenhum' | 'aprovar' | 'recusar'>('nenhum');
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function aprovar() {
    setErro(null);
    setOcupado(true);
    try {
      await api.paidMediaAprovarPlano(planId, {
        reason: motivo.trim(),
        // O valor ECOADO é o que esta tela mostrou. O servidor recusa se ele
        // mudou desde então, em vez de aprovar outra coisa.
        confirmed_budget_minor: valorMinor,
      });
      setModo('nenhum');
      setMotivo('');
      aoDecidir();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível falar com a API.');
    } finally {
      setOcupado(false);
    }
  }

  async function recusar() {
    setErro(null);
    setOcupado(true);
    try {
      await api.paidMediaRejeitarPlano(planId, { reason: motivo.trim() });
      setModo('nenhum');
      setMotivo('');
      aoDecidir();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível falar com a API.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section className="mt-8" aria-labelledby="decisao">
      <h2 id="decisao" className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Decisão
      </h2>
      <p className="mt-1 text-xs text-ink-muted">
        Aprovar autoriza este valor a sair do envelope quando a campanha for criada. Quem montou o
        plano não pode aprová-lo.
      </p>

      <div className="mt-3">
        <label htmlFor="motivo-da-decisao" className="block text-xs text-ink-muted">
          Motivo (fica no registro do plano)
        </label>
        <input
          id="motivo-da-decisao"
          value={motivo}
          autoComplete="off"
          onChange={(e) => setMotivo(e.target.value)}
          className="mt-1 w-full max-w-xl rounded border border-line px-2 py-1 text-sm"
        />
      </div>

      {modo === 'nenhum' ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setModo('aprovar')}
            className="rounded border border-accent-border bg-accent-strong px-3 py-1 text-sm text-white"
          >
            Aprovar plano
          </button>
          <button
            type="button"
            onClick={() => setModo('recusar')}
            className="rounded border border-line-strong px-3 py-1 text-sm hover:bg-surface-raised"
          >
            Recusar plano
          </button>
        </div>
      ) : null}

      {modo === 'aprovar' ? (
        <Confirmacao
          titulo="Aprovar este plano"
          oQueVaiAcontecer="Aprovar autoriza a criação da campanha com o valor abaixo. A campanha ainda nasce pausada, e ativar é outro ato."
          numeros={[
            { rotulo: 'Valor autorizado', valor: valorFormatado },
            {
              rotulo: 'Disponível no envelope hoje',
              valor: disponivelAntes ?? 'nenhum envelope aprovado',
            },
            {
              rotulo: 'Sobra depois da reserva',
              valor: disponivelDepois ?? 'não calculável sem envelope',
            },
          ]}
          textoDoBotao="Confirmar aprovação"
          aoConfirmar={aprovar}
          aoCancelar={() => setModo('nenhum')}
          ocupado={ocupado}
          impedimento={motivo.trim().length < 3 ? 'Escreva o motivo antes de confirmar.' : null}
        />
      ) : null}

      {modo === 'recusar' ? (
        <div className="mt-3 rounded border border-line p-3">
          <p className="text-sm">
            Recusar não gasta nada e não cria nada. O motivo fica registrado para quem for montar a
            próxima proposta.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={recusar}
              disabled={ocupado || motivo.trim().length < 3}
              className="rounded border border-accent-border bg-accent-strong px-3 py-1 text-sm text-white disabled:opacity-50"
            >
              Confirmar recusa
            </button>
            <button
              type="button"
              onClick={() => setModo('nenhum')}
              disabled={ocupado}
              className="rounded border border-line-strong px-3 py-1 text-sm hover:bg-surface-raised disabled:opacity-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : null}

      {erro === null ? null : (
        <p className="mt-3 rounded border border-danger-border bg-danger-surface p-3 text-sm">
          {erro}
        </p>
      )}
    </section>
  );
}

// ===========================================================================
// Criar a campanha, pausada (§6 passo 9, §248)
// ===========================================================================

function CriarCampanha({
  planId,
  valorMinor,
  valorFormatado,
  tipo,
  disponivelAntes,
  disponivelDepois,
  contaSugerida,
  portaoPassou,
  provedorConfigurado,
  motivoDoPortao,
  aoCriar,
}: {
  planId: string;
  valorMinor: number;
  valorFormatado: string;
  tipo: string;
  disponivelAntes: string | null;
  disponivelDepois: string | null;
  contaSugerida: string;
  portaoPassou: boolean;
  provedorConfigurado: boolean;
  motivoDoPortao: string;
  aoCriar: () => void;
}) {
  const [nome, setNome] = useState('');
  const [conta, setConta] = useState(contaSugerida);
  const [confirmando, setConfirmando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function criar() {
    setErro(null);
    setOcupado(true);
    try {
      await api.paidMediaCriarCampanha(planId, {
        name: nome.trim(),
        ad_account_id: conta.trim(),
        confirmed_budget_minor: valorMinor,
      });
      setConfirmando(false);
      aoCriar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível falar com a API.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section className="mt-8" aria-labelledby="criar-campanha">
      <h2
        id="criar-campanha"
        className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
      >
        Criar a campanha na Meta
      </h2>
      <p className="mt-1 text-xs text-ink-muted">
        A campanha nasce PAUSADA e o orçamento é reservado no envelope antes de qualquer objeto
        existir lá. Ativar é um ato separado.
      </p>

      {portaoPassou ? null : (
        <p className="mt-3 rounded border border-warn-border bg-warn-surface p-3">
          O portão recusou, e por isso criar está desligado. {motivoDoPortao}
        </p>
      )}
      {provedorConfigurado ? null : (
        <p className="mt-3 rounded border border-warn-border bg-warn-surface p-3">
          Não existe conexão com a Meta neste ambiente, então criar campanha está desligado. Nada é
          reservado nem enviado por engano.
        </p>
      )}

      <div className="mt-3 flex flex-wrap gap-3">
        <div className="grow">
          <label htmlFor="nome-da-campanha" className="block text-xs text-ink-muted">
            Nome da campanha
          </label>
          <input
            id="nome-da-campanha"
            value={nome}
            autoComplete="off"
            onChange={(e) => setNome(e.target.value)}
            className="mt-1 w-full rounded border border-line px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label htmlFor="conta-de-anuncio" className="block text-xs text-ink-muted">
            Conta de anúncio
          </label>
          <input
            id="conta-de-anuncio"
            value={conta}
            autoComplete="off"
            onChange={(e) => setConta(e.target.value)}
            className="mt-1 rounded border border-line px-2 py-1 text-sm"
          />
        </div>
      </div>

      {confirmando ? (
        <Confirmacao
          titulo="Criar a campanha pausada"
          oQueVaiAcontecer="Vai reservar o orçamento no envelope e criar os objetos na Meta, todos pausados. Nada começa a gastar agora."
          numeros={[
            { rotulo: 'Orçamento reservado', valor: valorFormatado, detalhe: tipo },
            {
              rotulo: 'Disponível no envelope antes',
              valor: disponivelAntes ?? 'nenhum envelope aprovado',
            },
            {
              rotulo: 'Sobra depois',
              valor: disponivelDepois ?? 'não calculável sem envelope',
            },
            { rotulo: 'Conta de anúncio', valor: conta.trim() || 'não informada' },
          ]}
          textoDoBotao="Confirmar e criar pausada"
          aoConfirmar={criar}
          aoCancelar={() => setConfirmando(false)}
          ocupado={ocupado}
          impedimento={
            nome.trim().length === 0
              ? 'Dê um nome à campanha antes de confirmar.'
              : conta.trim().length === 0
                ? 'Informe a conta de anúncio antes de confirmar.'
                : null
          }
        />
      ) : (
        <button
          type="button"
          onClick={() => setConfirmando(true)}
          disabled={!portaoPassou || !provedorConfigurado}
          className="mt-3 rounded border border-accent-border bg-accent-strong px-3 py-1 text-sm text-white disabled:opacity-50"
        >
          Criar campanha pausada
        </button>
      )}

      {erro === null ? null : (
        <p className="mt-3 rounded border border-danger-border bg-danger-surface p-3 text-sm">
          {erro}
        </p>
      )}
    </section>
  );
}

// ===========================================================================
// As campanhas do plano, e a ativação (§6 passo 10)
// ===========================================================================

function Campanhas({
  campanhas,
  provedorConfigurado,
  aoAtualizar,
}: {
  campanhas: CampanhaNaTela[];
  provedorConfigurado: boolean;
  aoAtualizar: () => void;
}) {
  const [ativando, setAtivando] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function ativar(c: CampanhaNaTela) {
    setErro(null);
    setOcupado(true);
    try {
      await api.paidMediaAtivarCampanha(c.campaign_id, {
        confirmed_budget_minor: c.budget.minor,
      });
      setAtivando(null);
      aoAtualizar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não foi possível falar com a API.');
    } finally {
      setOcupado(false);
    }
  }

  if (campanhas.length === 0) {
    return (
      <section className="mt-8" aria-labelledby="campanhas-do-plano">
        <h2
          id="campanhas-do-plano"
          className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
        >
          Campanhas deste plano
        </h2>
        <p className="mt-3 rounded-[11px] border border-line bg-surface-sunken p-4 text-ink-muted">
          Nenhuma campanha criada a partir deste plano. Nada está reservado e nada está gastando.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-8" aria-labelledby="campanhas-do-plano">
      <h2
        id="campanhas-do-plano"
        className="text-xs font-semibold uppercase tracking-wider text-ink-muted"
      >
        Campanhas deste plano
      </h2>

      <ul className="mt-3 space-y-3">
        {campanhas.map((c) => (
          <li
            key={c.campaign_id}
            className="rounded-card border border-line bg-card p-4 shadow-card"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium">{c.name}</span>
              <span className="text-xs">{c.requested_state_label}</span>
            </div>
            <p className="mt-1 text-xs text-ink-muted">
              {c.budget.formatado} · gasto {c.spent.formatado} · objetos na Meta:{' '}
              {formatNumber(c.provider_object_counts.adsets)} conjunto(s),{' '}
              {formatNumber(c.provider_object_counts.ads)} anúncio(s)
            </p>
            <p className="mt-1 text-xs text-ink-muted">
              Pedimos: <strong>{c.requested_state_in_spec}</strong> · A Meta responde:{' '}
              {c.never_reconciled ? (
                <span className="text-ink-faint">nunca perguntamos</span>
              ) : (
                <strong>{c.provider_effective_state}</strong>
              )}
            </p>

            {c.requested_state === 'paused' && provedorConfigurado ? (
              ativando === c.campaign_id ? (
                <Confirmacao
                  titulo="Ativar a campanha"
                  oQueVaiAcontecer="A partir daqui a campanha começa a entregar e a gastar de verdade na Meta. O portão inteiro é reavaliado antes de ligar."
                  numeros={[
                    { rotulo: 'Orçamento que vai ser gasto', valor: c.budget.formatado },
                    { rotulo: 'Já gasto até agora', valor: c.spent.formatado },
                    {
                      rotulo: 'Termina em',
                      valor:
                        c.end_at === null ? 'sem data de fim definida' : formatInstant(c.end_at),
                    },
                  ]}
                  textoDoBotao="Confirmar e ativar"
                  aoConfirmar={() => ativar(c)}
                  aoCancelar={() => setAtivando(null)}
                  ocupado={ocupado}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setAtivando(c.campaign_id)}
                  className="mt-2 rounded border border-accent-border bg-accent-strong px-3 py-1 text-xs text-white"
                >
                  Ativar
                </button>
              )
            ) : null}
          </li>
        ))}
      </ul>

      {erro === null ? null : (
        <p className="mt-3 rounded border border-danger-border bg-danger-surface p-3 text-sm">
          {erro}
        </p>
      )}
    </section>
  );
}
